import {
  type DataMessagePartComponent,
  makeAssistantDataUI,
} from '@assistant-ui/react';
import React from 'react';

import type {ContentPart} from '../../chat/store/thread';
import type {ComposerReferencePart} from '../composer/slate';
import {ArtifactCollectionMentionPart} from './ArtifactCollectionMentionPart';
import {ConversationMentionPart} from './ConversationMentionPart';
import {MessageReferenceTag} from './ReferenceTag';

type WandbDataPartType = Extract<ContentPart['type'], `wandb-${string}`>;

const ReferenceMentionPart: DataMessagePartComponent<ComposerReferencePart> = ({
  data,
}) => <MessageReferenceTag part={data} />;

/**
 * WANDB_DATA_PART_RENDERERS maps each wandb-* store type to its data renderer,
 * or null when the type has no user-facing representation. A new wandb-* store
 * type fails to compile here until it is classified, and any renderer added is
 * registered and mounted automatically below.
 */
const WANDB_DATA_PART_RENDERERS: Record<
  WandbDataPartType,
  DataMessagePartComponent | null
> = {
  'wandb-entity': null,
  'wandb-project': null,
  'wandb-run': ReferenceMentionPart,
  'wandb-run-file': ReferenceMentionPart,
  'wandb-call': ReferenceMentionPart,
  'wandb-conversation': ConversationMentionPart,
  'wandb-artifact-collection': ArtifactCollectionMentionPart,
  'wandb-workspace-panel': ReferenceMentionPart,
  'wandb-web-page-state': null,
};

// NOTE: Built at module scope so the render refs stay stable.
const DATA_UIS = Object.entries(WANDB_DATA_PART_RENDERERS).flatMap(
  ([name, render]) =>
    render ? [{name, DataUI: makeAssistantDataUI({name, render})}] : []
);

/**
 * WBAgentDataUIs registers the wandb data-part renderers with AUI's data
 * registry. Mount once inside `AuiProvider`; it renders nothing itself, and the
 * renderers then resolve through `part.dataRendererUI` for any message role.
 */
export const WBAgentDataUIs = () => (
  <>
    {DATA_UIS.map(({name, DataUI}) => (
      <DataUI key={name} />
    ))}
  </>
);
